import React from 'react';
import isEqual from 'lodash/isEqual';
import uniqWith from 'lodash/uniqWith';
import ErrorBoundary from '@library/ErrorBoundary';
import { EditData, ViewData } from '@library/data-section';
import { useStoreContext } from '@/state';
import Edit from './edit';
import View from './view';
import './styles/index';

type Props = {
  statusPath: string;
  hoursMayVary?: boolean | null;
  temporaryClosures?: {
    id?: number;
    start_date: string;
    end_date: string | null;
    reason: string;
  }[];
  mayRequireAppointment?: boolean | null;
  permanentlyClosed?: boolean | null;
  userEditingStrategy: 'update' | 'suggest' | 'none';
  status?: {
    warnings?: string[];
    reason?: string;
    operating_normally?: boolean;
    changes_at?: string;
  };
};

// Atlas has a unique index on (place, start_date, end_date, reason), so duplicates are
// dropped on the way out rather than while editing.
const sameClosure = (a, b) =>
  a.start_date === b.start_date &&
  a.end_date === b.end_date &&
  a.reason === b.reason;

const Closures = function(props: Props){
  const { analytics } = useStoreContext();
  const [saved, setSaved] = React.useState(false);
  const [status, setStatus] = React.useState({
    temporaryClosures: props.temporaryClosures || [],
    hoursMayVary: props.hoursMayVary,
    mayRequireAppointment: props.mayRequireAppointment,
    permanentlyClosed: props.permanentlyClosed
  });
  const [savedStatus, setSavedStatus] = React.useState(status);

  const readOnly = props.userEditingStrategy === 'none';
  const dirty = !isEqual(status, savedStatus);

  return (
    <ErrorBoundary>
      <div data-cy="closures" className="editor__data">
        {readOnly ? (
          <>
            <h4 className="mt-5" id="Status">
              {'Status'}
            </h4>
            <ViewData
              cy="closures-view"
              sectionTitle="Status"
              userEditingStrategy={props.userEditingStrategy}
              show
              onClick={() => {}}
            >
              <View {...savedStatus} status={props.status} />
            </ViewData>
          </>
        ) : (
          <EditData
            cy="closures-edit"
            sectionTitle="Status"
            analytics={analytics}
            userEditingStrategy={props.userEditingStrategy}
            show
            disable={!dirty}
            disableCancel={!dirty}
            cancelLabel="Discard changes"
            params={{
              listing: {
                temporary_closures: uniqWith(
                  status.temporaryClosures,
                  sameClosure
                ),
                details: {
                  hours_may_vary: status.hoursMayVary,
                  may_require_appointment: status.mayRequireAppointment,
                  permanently_closed: status.permanentlyClosed
                }
              }
            }}
            cancel={() => setStatus(savedStatus)}
            onSuccess={() => {
              setSavedStatus(status);
              setSaved(true);
            }}
          >
            <Edit
              {...status}
              onChange={(change) => setStatus((s) => ({ ...s, ...change }))}
            />
            <div className="editor__listing__hours__save-notice" role="status">
              {dirty ? 'Unsaved changes' : saved && 'Saved Successfully!'}
            </div>
          </EditData>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default Closures;
